import { getThemes, getTokens } from "../config";
import { THEME_CLASSNAME_PREFIX, THEME_NAME_SEPARATOR } from "../constants/constants";
import { getThemeUnwrapped } from "../hooks/getThemeUnwrapped";
const emptyState = { name: "" };
function hasNoThemeUpdatingProps(props) {
  return !(props.name || props.componentName || props.inverse || props.reset);
}
let uid = 0;
class ThemeManager {
  constructor(props = {}, parentManagerIn) {
    this.props = props;
    this.id = uid++;
    this.isComponent = false;
    this.themeListeners = /* @__PURE__ */ new Set();
    this.parentManager = null;
    this.state = emptyState;
    this._allKeys = null;
    if (parentManagerIn === "root") {
      this.updateState(props, false);
      return;
    }
    const parentManager = getNonComponentParentManager(parentManagerIn);
    if (!parentManager) {
      if (process.env.NODE_ENV !== "production") {
        throw new Error(
          `No parent manager given, this is likely due to duplicated Tamagui dependencies. Check your lockfile for mis-matched versions.`
        );
      }
      throw `\u274C`;
    }
    if (hasNoThemeUpdatingProps(props)) {
      return parentManager;
    }
    if (parentManager) {
      this.parentManager = parentManager;
    }
    if (this.updateState(props, false)) {
      return;
    }
    return parentManager || this;
  }
  updateState(props = this.props || {}, shouldNotify = true) {
    const isChanging = (() => {
      if (props.forceTheme) {
        this.state.theme = props.forceTheme;
        this.state.name = props.name || "";
        return true;
      }
      const nextState = this.getStateIfChanged(props);
      if (nextState) {
        this.props = props;
        this.state = nextState;
        return true;
      }
    })();
    if (isChanging) {
      const names = this.state.name.split("_");
      const lastName = names[names.length - 1][0];
      this.isComponent = lastName[0] === lastName[0].toUpperCase();
      this._allKeys = null;
      if (process.env.NODE_ENV === "development") {
        this["_numChangeEventsSent"] ??= 0;
        this["_numChangeEventsSent"]++;
      }
      if (shouldNotify) {
        this.notify(!!props.forceTheme);
      }
      return this.state;
    }
  }
  getStateIfChanged(props = this.props, state = this.state, parentManager = this.parentManager) {
    const _ = this.getState(props, parentManager);
    if (state && state !== emptyState && !_) {
      return parentManager == null ? void 0 : parentManager.state;
    }
    if (this.getStateShouldChange(_, state)) {
      return _;
    }
  }
  getStateShouldChange(nextState, state = this.state) {
    if (!(nextState == null ? void 0 : nextState.theme) || nextState.theme === (state == null ? void 0 : state.theme)) {
      return false;
    }
    return true;
  }
  getState(props = this.props, parentManager = this.parentManager) {
    return getState(props, parentManager) || (process.env.TAMAGUI_TARGET === "native" ? (parentManager == null ? void 0 : parentManager.state) || null : null);
  }
  get allKeys() {
    var _a;
    this._allKeys ||= /* @__PURE__ */ new Set([
      ...((_a = this.parentManager) == null ? void 0 : _a.allKeys) || [],
      ...Object.keys(this.state.theme || {})
    ]);
    return this._allKeys;
  }
  // gets value going up to parents
  getValue(key, state) {
    if (!key)
      return;
    let theme = (state || this.state).theme;
    let manager = this;
    while (theme && manager) {
      if (key in theme) {
        return theme[key];
      }
      manager = manager.parentManager;
      theme = manager == null ? void 0 : manager.state.theme;
    }
    const tokens = getTokens();
    if (key in tokens.color) {
      return tokens.color[key];
    }
  }
  notify(forced = false) {
    this.themeListeners.forEach((cb) => cb(this.state.name, this, forced));
  }
  onChangeTheme(cb, debugId) {
    if (process.env.NODE_ENV === "development" && debugId) {
      this._listeningIds ??= /* @__PURE__ */ new Set();
      this._listeningIds.add(debugId);
    }
    this.themeListeners.add(cb);
    return () => {
      this.themeListeners.delete(cb);
    };
  }
}
function getNextThemeClassName(name, props) {
  const next = `t_sub_theme ${THEME_CLASSNAME_PREFIX}${name}`;
  if (props.inverse || props.forceClassName || // light and dark inverse each other so force classname
  name === "light" || name === "dark") {
    const lessSpecificCN = props.componentName ? "" : next.includes("dark_") ? ` t_dark` : next.includes("light_") ? ` t_light` : "";
    return next + lessSpecificCN;
  }
  return next.replace("light_", "").replace("dark_", "");
}
function getState(props, parentManager) {
  var _a, _b, _c;
  const themes = getThemes();
  if (props.name && props.reset) {
    throw new Error("Cannot reset + set new name");
  }
  if (!props.name && !props.inverse && !props.reset && !props.componentName) {
    return null;
  }
  if (props.reset && !(parentManager == null ? void 0 : parentManager.parentManager)) {
    if (process.env.NODE_ENV === "development") {
      console.warn("Cannot reset no grandparent exists");
    }
    return null;
  }
  let result = null;
  const nextName = props.reset ? ((_b = (_a = parentManager == null ? void 0 : parentManager.parentManager) == null ? void 0 : _a.state) == null ? void 0 : _b.name) || "" : props.name || "";
  const { componentName } = props;
  const parentName = ((_c = parentManager == null ? void 0 : parentManager.state) == null ? void 0 : _c.name) || "";
  const base = parentName.split(THEME_NAME_SEPARATOR);
  const lastSegment = base[base.length - 1];
  const isParentComponentTheme = parentName && lastSegment[0].toUpperCase() === lastSegment[0];
  if (isParentComponentTheme) {
    base.pop();
  }
  const parentBaseTheme = isParentComponentTheme ? base.slice(0, base.length).join(THEME_NAME_SEPARATOR) : parentName;
  const max = base.length;
  const min = componentName && !nextName ? max : 0;
  if (process.env.NODE_ENV === "development" && typeof props.debug === "string") {
    console.groupCollapsed("ThemeManager.getState()");
    console.log({
      props,
      parentName,
      parentBaseTheme,
      base,
      min,
      max,
      isParentComponentTheme
    });
  }
  for (let i = max; i >= min; i--) {
    let prefix = base.slice(0, i).join(THEME_NAME_SEPARATOR);
    if (props.inverse) {
      prefix = inverseThemeName(prefix);
    }
    let potentials = [];
    if (prefix && prefix !== parentBaseTheme) {
      potentials.push(prefix);
    }
    if (nextName) {
      potentials.unshift(prefix ? `${prefix}_${nextName}` : nextName);
    }
    if (i === 1) {
      const lastSegment2 = potentials.findIndex((x) => !x.includes("_"));
      if (lastSegment2 > 0) {
        potentials.splice(lastSegment2, 0, nextName);
      }
    }
    if (componentName) {
      let componentPotentials = [];
      if (nextName) {
        const beforeSeparator = prefix.slice(0, prefix.indexOf(THEME_NAME_SEPARATOR));
        componentPotentials.push(`${beforeSeparator}_${nextName}_${componentName}`);
      }
      componentPotentials.push(`${prefix}_${componentName}`);
      if (nextName) {
        const prefixLessOne = base.slice(0, i - 1).join(THEME_NAME_SEPARATOR);
        if (prefixLessOne) {
          const lessSpecific = `${prefixLessOne}_${nextName}_${componentName}`;
          componentPotentials.unshift(lessSpecific);
        }
        const moreSpecific = `${prefix}_${nextName}_${componentName}`;
        componentPotentials.unshift(moreSpecific);
      }
      potentials = [...componentPotentials, ...potentials];
    }
    const found = potentials.find((t) => t in themes);
    if (process.env.NODE_ENV === "development" && typeof props.debug === "string") {
      console.log(" - ", { found, potentials, parentManager });
    }
    if (found) {
      result = {
        name: found,
        theme: getThemeUnwrapped(themes[found]),
        className: getNextThemeClassName(found, props),
        parentName
      };
      break;
    }
  }
  if (process.env.NODE_ENV === "development" && typeof props.debug === "string") {
    console.warn("ThemeManager.getState():", {
      result
    });
    console.trace();
    console.groupEnd();
  }
  return result;
}
const inverseThemeName = (themeName) => {
  return themeName.startsWith("light") ? themeName.replace(/^light/, "dark") : themeName.replace(/^dark/, "light");
};
function getNonComponentParentManager(themeManager) {
  let res = themeManager;
  while (res) {
    if (res == null ? void 0 : res.isComponent) {
      res = res.parentManager;
    } else {
      break;
    }
  }
  return res || null;
}
export {
  ThemeManager,
  getNonComponentParentManager,
  hasNoThemeUpdatingProps
};
//# sourceMappingURL=ThemeManager.js.map
